import React, { useState, useEffect, useMemo } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons'

import { onModalMessage } from 'src/utils/messages/modalChannel'

// UK-only Gift Aid opt-in. The modal content (eligibility copy + the
// two buttons) is served by Widgets::ModalsController#gift_aid. v7
// triggers it through the openModal pipeline so the dialog can land
// either inside the donate widget (mode 1 / mode 3 fallback) or at
// the host page (mode 2) without the donor seeing the difference.
//
// `id` is keyed on widget_instance_token rather than on a
// partner_item identifier so two donate widgets on the same page
// get distinct opener routing — a donor who hits the green widget's
// Gift Aid trigger will see green opt-in flow back to the green
// widget, not whichever instance happened to render first.
//
// onModalMessage listens on both host.onAll('toOpener', filter) and
// client.onAll('giftAidConfirmed'), so the same subscription works
// regardless of which mode the modal landed in.
const useGiftAid = ({ i18n, partnerItem, partner_items, src, id }) => {
  const [ value, setValue ] = useState(false)

  const available = useMemo(() =>
    partnerItem.beneficiary.just_giving || (
      partnerItem.beneficiary.id == '_multiple' &&
      partner_items.some(pi => pi.beneficiary.just_giving)
    )
  , [ partnerItem.external_id ])

  useEffect(() => {
    if (!available) return
    return onModalMessage('giftAidConfirmed', () => setValue(true))
  }, [ available ])

  const render = () =>
    available && (
      value ? (
        <p className="text-sm text-success inline-flex items-center gap-2">
          <FontAwesomeIcon icon={faCheckCircle} />
          Opted into Gift Aid
        </p>
      ) : (
        <button
          type="button"
          data-plg-modal-label="Gift Aid"
          data-plg-modal-src={src}
          data-plg-modal-id={id}
          data-plg-modal-max-width="36rem"
          className="btn-link text-sm underline underline-offset-2"
        >
          Add Gift Aid to your donation
        </button>
      )
    )

  // Gated the same way useCanadianReceipt gates its own value (`available
  // && checked`) — value is set by the giftAidConfirmed message and never
  // reset, so without this a donor who opts in and then switches to a
  // non-eligible org would still read as opted-in downstream even though
  // render() already correctly stops showing the confirmed state (it
  // gates on `available` too — this only affects consumers that read
  // `.value` directly: useRecord's order draft, useDonation's
  // collectAddress, and paymentMethodConfig's US-receipt-only filter).
  return { available, render, value: available && value }
}

export default useGiftAid
